import { Locale } from "@plex/culture-core";
import { locales } from "@plex/culture-locales";

import { settings as appSettings } from "config";
import { defaultLocale } from "infrastructure/localization";
import { translations } from "translations";

import { ITranslationMessagesLoadParam, IAppTranslationState, ITranslationAttributes, ISettingsState, getTranslationMessages, ITranslationMessagesGetResponse } from ".";

export const isSupportedLocale = (locale: string): boolean => {
  return locale in locales;
};

const getBrowserLanguage = () => {
  return (navigator.languages && navigator.languages[0]) || navigator.language || navigator["userLanguage"];
};

const getSupportedBrowserLanguage = () => {
  const browserLanguage = getBrowserLanguage();

  if (isSupportedLocale(browserLanguage)) {
    return browserLanguage;
  }

  const langtag = browserLanguage.split("-")[0];
  return isSupportedLocale(langtag) ? langtag : undefined;
};

export const findTranslationAttributes = (appTranslation: IAppTranslationState, messagePrefix: string): ITranslationAttributes | undefined => {
  if (appTranslation.attributes) {
    return appTranslation.attributes[messagePrefix];
  }
};

export const loadCldrData = () => {
  for (const l in locales) {
    Locale.load(locales[l]);
  }
};

export const getOptions = (payload: ITranslationMessagesLoadParam, settings: ISettingsState, appTranslation: IAppTranslationState) => {
  const isFirstTimeInitialization = !settings.localSettings.locale;

  let locale = settings.localSettings.locale;
  let loadNewLocale = false;

  if (payload) {
    locale = payload.locale || locale || getSupportedBrowserLanguage();
    loadNewLocale = payload.loadNewLocale;
  }

  if (!isSupportedLocale(locale) && (isFirstTimeInitialization || !appTranslation.isInitialized)) {
    locale = defaultLocale.localeIdentifier;
  }

  return {
    locale: locale,
    loadNewLocale: loadNewLocale,
    isFirstTimeInitialization: isFirstTimeInitialization
  };
};

export const loadTranslationMessages = async (
  messagePrefix: string,
  appTranslation: IAppTranslationState,
  opt: ReturnType<typeof getOptions>
): Promise<ITranslationMessagesGetResponse> => {
  const attributes = findTranslationAttributes(appTranslation, messagePrefix);
  if (appSettings.translation.useLocalTranslations) {
    return {
      lastModified: new Date(),
      messagePrefix: messagePrefix,
      messages: translations[opt.locale.split("-")[0]]
    };
  } else {
    return await getTranslationMessages({
      locale: opt.locale,
      messagePrefix: messagePrefix,
      lastModified: attributes ? attributes.lastModified : undefined,
      noCache: opt.loadNewLocale
    });
  }
};
